import React, { useState } from 'react'

import { ReactComponent as CsnLogo } from 'assets/images/logo_csn.svg'
import Logotipo from 'assets/images/logotipo_csn.png'
import { Grid, Text, Span, Card, Row, Button, Col, Image, TableFooter } from 'components'
import LoadingComponent from 'components/LoadingComponent/LoadingComponent'
import { ExcelIcon, PdfIcon } from 'components/Svg'
import { useModal, useReportPrint } from 'hooks'
import moment from 'moment'
import { ReportCurrentContractService } from 'services/NGT/reportCurrentContract'
import { COLORS, SPACING, FONTS, TYPOGRAPHY, FORMAT } from 'utils'

import TableReportsCurrent from './tableCurrent'
import LoadingIcon from 'components/Icons/LoadingIcon/LoadingIcon'
import { ArrowBack } from '@mui/icons-material'

export interface ReportsCurrent {
  tableData: any
  visualizar: Function
  contratos: any
  setTableData: (data: any) => void
}

export const ReportsCurrent: React.FC<ReportsCurrent> = ({
  tableData,
  visualizar,
  contratos,
  setTableData
}) => {
  const tableDataArray = tableData?.resultado
  const [hideForPint] = useState<boolean>(false)
  const { handlePrintTable } = useReportPrint()
  const [page, setPage] = useState(0);
  const [totalContracts, setTotalContracts] = useState<Number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(15)
  const [pageCount, setPageCount] = useState<number>(0)
  const [loading, setLoading] = useState<string>('idle')
  const [loadingPDF, setLoadingPDF] = useState<boolean>(false)
  const [textLoadingPDF, setTextLoadingPDF] = useState<string>('')
  const [textLoadingExcel, setTextLoadingExcel] = useState<string>(
    'Começamos a gerar o seu Excel, Aguarde um momento.'
  )

  const [downloadExcel, setDownloadExcel] = useState<boolean>(false)
  const { showModal } = useModal()

  const handleChangePage = (obj: { newPage?: number, isForPdfGeneration?: boolean }) => {
    const { newPage, isForPdfGeneration } = obj
    if (!isForPdfGeneration) setLoading('loading')

    !isForPdfGeneration && setPage(newPage)
    contratos.pageSize = isForPdfGeneration ? totalContracts : rowsPerPage
    contratos.pageNumber = isForPdfGeneration ? 1 : newPage
    if (isForPdfGeneration) {
      setLoadingPDF(true)
      setTextLoadingPDF(`Começamos a gerar seu PDF, Aguarde um momento.`)
    }
    ReportCurrentContractService()
      .visualizar(contratos)
      .then((response: any) => {
        if (!isForPdfGeneration) {
          setPageCount(response?.data?.pageCount)
          setTableData(response?.data?.items[0])
          setTotalContracts(response?.data?.totalItemCount)
          setLoading('idle')
          setTextLoadingPDF('')
          setLoadingPDF(false)
        } else {
          handleGeneratePDF(response?.data?.items[0].resultado)
          setLoading('idle')
          setTextLoadingPDF('')
          setLoadingPDF(false)
        }

      })
      .catch((error) => {
        if (error?.response?.data?.mensagem) {
          showModal({
            hideButtonCancel: true,
            message: error?.response?.data?.mensagem || 'Erro ao buscar pagina.',
            txtButtonConfirm: 'OK',
            type: 'alert'
          })
          setLoading('idle')
          return
        }
        setLoading('idle')
        setTextLoadingPDF('')
        setLoadingPDF(false)
      })
  }

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(1)
  }

  const handleExcel = () => {
    setDownloadExcel(true)
    ReportCurrentContractService()
      .visualizarExcel(contratos)
      .then((response) => {
        const url =
          'data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,' +
          response.data
        const downloadLink = document.createElement('a')
        downloadLink.href = url
        downloadLink.download = 'Relatorio-Contrato-Vigente.xlsx'
        document.body.appendChild(downloadLink)
        downloadLink.click()
        document.body.removeChild(downloadLink)
        setDownloadExcel(false)
      }).catch(() => {
        setLoading('idle')
        showModal({
          hideButtonCancel: true,
          message: 'Não foi possivel obter o dados da pesquisa',
          txtButtonConfirm: 'Não foi possivel baixar excel.',
          type: 'error'
        })
        setDownloadExcel(false);
      })
  }

  const HeaderSupplierTitle = () => (
    <Card
      backgroundColor={COLORS.headerColor}
      padding={SPACING.spacing20}
      marginBottom={`-${SPACING.spacing15}`}
    >
      <Row alignItems="center" justifyContent="left">
        <Col gap=".7rem" alignItems="center" justifyContent="left">
          <CsnLogo width="62" height="40" />
          <Image src={Logotipo} width="3.8rem" />
        </Col>
      </Row>
      <Span
        text="RELATÓRIO DE CONTRATOS VIGENTES"
        color={COLORS.blueFonts}
        fontSize="24px"
        fontWeight={FONTS.bold}
        textTransform="uppercase"
        style={{
          display: 'flex',
          justifyContent: 'center'
        }}
      />
      <Span
        text={`Total de Contratos: ${totalContracts}`}
        color={COLORS.blueFonts}
        fontSize="16px"
        fontWeight={FONTS.bold}
        textTransform="uppercase"
        style={{
          display: 'flex',
          justifyContent: 'center'
        }}
      />
    </Card>
  )

  const handleGeneratePDF = (tableDataPdfArray: any) => {
    handlePrintTable({
      data: {
        body: tableDataPdfArray.map((row) => [
          row.cdContrato || '---',
          row.empresa || '---',
          row.cdCentro || '---',
          row.centro || '---',
          row.de || '---',
          row.dir || '---',
          row.gg || '---',
          row.ger || '---',
          row.contratada || '---',
          row.subcontratada || '---',
          row.escopo.slice(0, 50) || '---',
          row.inicio || '---',
          row.fim || '---',
          row.encerrado || '---',
          row.contraparte || '---',
          FORMAT.currency(row.valorAtual) || '---'
        ]),
        foot: [
          `TOTAL DE CONTRATADAS: ${tableData?.totalContratada?.toLocaleString()}`,
          `TOTAL DE SUBCONTRATADAS: ${tableData?.totalSubcontratada?.toLocaleString()}`,
          `TOTAL GERAL: ${tableData?.totalGeral?.toLocaleString()}`,
          `TOTAL DE CONTRAPARTE: ${tableData?.totalContraparte?.toLocaleString()}`
        ],
        header: [
          'Contrato',
          'Empresa',
          'Código',
          'Centro',
          'DE',
          'DIR',
          'GG',
          'GER',
          'Contratada',
          'SubContratada',
          'Escopo',
          'Início',
          'Fim',
          'Encerrado',
          'Contraparte',
          'Valor Atual'
        ],
        tipo: tableData?.tipo,
        title: 'RELATÓRIO DE CONTRATOS VIGENTES'
      },
      filename: `relatório-de-contratos-vigentes-${moment().format(
        'DD-MM-YYYY'
      )}`
    })
  }

  const handlePdfIconClick = () => {
    setLoadingPDF(true);
    handleChangePage({ isForPdfGeneration: true });
  };

  return (
    <>
      <Card
        marginTop={SPACING.spacing20}
        marginLeft={SPACING.spacing20}
        marginRight={SPACING.spacing20}
        marginBottom={SPACING.spacing100}
        padding={SPACING.spacing20}
        boxShadow={`0 0 1em ${COLORS.lightGray}`}
      >
        <div id="divToPrint">
          {hideForPint ? (
            <></>
          ) : (
            <>
              <Grid gridCols="10fr 1fr" gridGap="1rem" margin="1rem 0 0 0">
                <Text>
                  <Span
                    fontSize={TYPOGRAPHY.subtitleSize20}
                    fontWeight={FONTS.bold}
                    text="TIPO: "
                  />
                  <Span
                    fontSize={TYPOGRAPHY.subtitleSize20}
                    text={tableData?.tipo || '-----'}
                  />
                </Text>
                <a style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.3rem', whiteSpace: 'nowrap' }}>
                  {downloadExcel ? (
                    <>
                      <Grid justifyItems="center" gridCols="1fr" gridGap="1rem">
                        <LoadingIcon colorIcon={COLORS.blueFonts} />
                        <Span text={`${textLoadingExcel}`} />
                      </Grid>
                    </>
                  ) : (
                    <ExcelIcon style={{ marginLeft: SPACING.spacing5 }} onClick={handleExcel} />
                  )}


                  {!loadingPDF && <PdfIcon onClick={handlePdfIconClick} />}
                  {loadingPDF && (
                    <Grid alignItems="center" justifyContent='flex-end' justifyItems="center" gridCols="1fr" gridGap="1rem">
                      <LoadingIcon colorIcon={COLORS.blueFonts} />
                      <Span text={textLoadingPDF} />
                    </Grid>
                  )}
                  <Button
                    onClick={() => visualizar(false)}
                    startIcon={<ArrowBack />}
                    text="Voltar"
                    variantButton="outlined"
                    marginRight={SPACING.spacing15}
                    marginLeft={SPACING.spacing15}
                    textColor={COLORS.blueFonts} // Texto azul escuro
                    borderColor={COLORS.blueFonts} // Borda azul claro
                    boxShadow={`0 2px 4px ${COLORS.gray}`}
                    height='3rem' // Sombra cinza
                    style={{ textAlign: 'center' }}
                    backgroundColor={COLORS.white}
                  />
                </a>
              </Grid>
            </>
          )}
          <HeaderSupplierTitle />
          <TableReportsCurrent tableData={tableDataArray} />
          <Grid
            padding={SPACING.spacing15}
            backgroundColor={COLORS.lightGray}
            gridCols="2fr 2.5fr 2fr 2fr"
            gridGap="1rem"
            margin="1rem 0 0 0"
          >
            <Span
              fontSize={TYPOGRAPHY.bodySize16}
              fontWeight={FONTS.bold}
              text={`TOTAL DE CONTRATADAS: ${tableData?.totalContratada || 0}`}
            />
            <Span
              fontSize={TYPOGRAPHY.bodySize16}
              fontWeight={FONTS.bold}
              text={`TOTAL DE SUBCONTRATADAS: ${tableData?.totalSubcontratada || 0}`}
            />
            <Span
              fontSize={TYPOGRAPHY.bodySize16}
              fontWeight={FONTS.bold}
              text={`TOTAL DE GERAL: ${tableData?.totalGeral || 0}`}
            />
            <Span
              fontSize={TYPOGRAPHY.bodySize16}
              fontWeight={FONTS.bold}
              text={`TOTAL DE CONTRAPARTE: ${tableData?.totalContraparte || 0}`}
            />
          </Grid>
          {!hideForPint ? (
            <></>
          ) : (
            <Text margintop={SPACING.spacing15}>
              <Span
                fontSize={TYPOGRAPHY.subtitleSize20}
                fontWeight={FONTS.bold}
                text="TIPO: "
              />
              <Span
                fontSize={TYPOGRAPHY.subtitleSize20}
                text={tableData?.tipo || '-----'}
              />
            </Text>
          )}
          <TableFooter
            page={page}
            totalItems={pageCount}
            rowsPerPage={rowsPerPage}
            handleChangePage={(newPage) => handleChangePage({ newPage: newPage })}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            typeFooter="online"
          />
          <Row
            marginTop={SPACING.spacing15}
            marginBottom={SPACING.spacing30}
            alignItems="center"
            justifyContent="end"
          >
            {/* <Button text="Voltar" variantButton="text" /> */}

          </Row>
        </div>
      </Card>
      {loading === 'loading' ? <LoadingComponent /> : null}
    </>
  )
}
